import { EOrderStatus, ERecieveMethod, ITableOrder } from 'models/orders'
import { roundToKopecks } from 'shared/utils/roundToKopecks'

export interface IOrderMoneyBreakdown {
  buyerDeliverySum: number
  platformDeliverySum: number
  sellerDeliverySum: number
  // Скидка промокода, которую оплатила площадка: продавцу платят с полной цены
  platformDiscountSum: number
  buyerTotal: number
  sellerPayout: number
  // Комиссия, которую площадка удержала: с отменённого и неоплаченного — ноль
  platformCommission: number
  platformProfit: number
}

/**
 * Пересчёт СДЭК после приёма посылки: сколько взяли по факту и насколько это
 * больше или меньше названного при оформлении. null — факта ещё нет или он совпал.
 * provider_cost не берём: при смене пункта отправления его перезаписывают
 */
export const getPvzDeliveryRecalc = (order: ITableOrder) => {
  const factCost = order.delivery_info?.fact_cost
  if (order.receive_method !== ERecieveMethod.PVZ || typeof factCost !== 'number') {
    return null
  }

  const calcCost = roundToKopecks(
    (order.discount_pvz_delivery_sum ?? 0) + (order.pvz?.delivery_sum ?? 0)
  )

  const diff = roundToKopecks(factCost - calcCost)
  return diff === 0 ? null : { factCost, diff }
}

/**
 * Кто сколько заплатил за заказ. Для новых ПВЗ-заказов берём сохранённые backend
 * доли, чтобы изменение политики не меняло историю. Для заказов без снимка
 * сохраняется прежний расчёт через pvz и discount_pvz_delivery_sum.
 */
export const getOrderMoneyBreakdown = (
  order: ITableOrder,
  commission: number,
  // курьерскую доставку админ правит руками — пока не сохранил, считаем по форме
  courierDeliverySum?: number
): IOrderMoneyBreakdown => {
  const isPvz = order.receive_method === ERecieveMethod.PVZ
  const isPickUpPoint = order.receive_method === ERecieveMethod.PICKUP_POINT

  const getBuyerDeliverySum = () => {
    if (isPickUpPoint) return 0
    if (isPvz) return order.pvz?.delivery_sum ?? 0
    return courierDeliverySum ?? order.delivery_info?.delivery_sum ?? 0
  }

  const buyerDeliverySum = getBuyerDeliverySum()
  const costSharing = isPvz ? order.delivery_info?.cost_sharing : undefined
  const coveredDeliverySum = isPvz ? order.discount_pvz_delivery_sum ?? 0 : 0
  const sellerDeliverySum = costSharing
    ? costSharing.seller_share
    : order.isFreeDeliveryToPvz === false
      ? coveredDeliverySum
      : 0
  const factCost = isPvz ? order.delivery_info?.fact_cost : undefined
  // Покупатель и продавец платят то, что им назвали при заказе, —
  // разницу с фактом СДЭК несёт площадка. Так же считает выгрузка бэкенда
  const platformDeliverySum = typeof factCost === 'number'
    ? roundToKopecks(
      factCost - (costSharing?.buyer_share ?? buyerDeliverySum) - sellerDeliverySum
    )
    : costSharing
      ? costSharing.platform_share
      : roundToKopecks(coveredDeliverySum - sellerDeliverySum)

  // С отменённого заказа площадка не получила комиссию и не заплатила за доставку:
  // деньги вернулись покупателю целиком. С неоплаченного брать ещё нечего
  const isPlatformIncomeLost =
    order.status === EOrderStatus.CANCELED ||
    order.status === EOrderStatus.NOT_PAID
  const platformDiscountSum = order.promo_details?.store_discount_impact ?? 0

  return {
    buyerDeliverySum: costSharing?.buyer_share ?? buyerDeliverySum,
    platformDeliverySum,
    sellerDeliverySum,
    platformDiscountSum,
    // total_received — факт оплаты: он же уменьшается при возврате товара из заказа,
    // и только у старых заказов его нет — там остаётся считать из частей
    buyerTotal:
      order.total_received ||
      roundToKopecks(
        (order.sum ?? 0) + (costSharing?.buyer_share ?? buyerDeliverySum)
      ),
    // full_sum бэкенд уже посчитал за вычетом комиссии и доставки за счёт продавца
    sellerPayout: order.full_sum ?? 0,
    platformCommission: isPlatformIncomeLost ? 0 : commission,
    platformProfit: isPlatformIncomeLost
      ? 0
      : roundToKopecks(commission - platformDeliverySum - platformDiscountSum)
  }
}
